/**
 * "Move to Forge" (ADR-023 D6): imports this builder session into a new Fias
 * Forge. The confirm dialog states the two facts the user must accept before
 * the click — it is one-way, and the builder session becomes read-only. On
 * success the page navigates to the forge; a 409 from the server (already
 * moved, or the arche already has a forge) offers that forge instead.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hammer, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { forgePagePath } from './ForgeMigratedBanner';

interface MoveToForgeButtonProps {
  sessionId: string;
  /** No arche yet (never saved/built): the server would refuse, so say so up front. */
  hasArche: boolean;
  /** Renders as a menu-style full-width button on small screens. */
  variant?: 'toolbar' | 'menu';
  className?: string;
}

/** `409 SESSION_ALREADY_MIGRATED` / `FORGE_EXISTS` carry the forge in the error envelope. */
function existingForgeIdFromError(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { error?: { forgeId?: unknown } } | undefined;
  const forgeId = body?.error?.forgeId;
  return typeof forgeId === 'string' && forgeId ? forgeId : null;
}

export function MoveToForgeButton({
  sessionId,
  hasArche,
  variant = 'toolbar',
  className,
}: MoveToForgeButtonProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<{ message: string; forgeId: string | null } | null>(null);

  const handleConfirm = async () => {
    setIsMoving(true);
    setError(null);
    try {
      const { forge } = await api.importForgeFromBuilder(sessionId);
      setOpen(false);
      navigate(forgePagePath(forge.id));
    } catch (err) {
      setError({
        message: err instanceof Error ? err.message : 'Could not move this session to Forge.',
        forgeId: existingForgeIdFromError(err),
      });
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <>
      <Button
        variant={variant === 'menu' ? 'ghost' : 'ghost'}
        size="sm"
        className={className}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
        title={hasArche ? undefined : 'Save or build once before moving to Forge'}
        disabled={!hasArche}
      >
        <Hammer className="h-4 w-4 mr-2" />
        Move to Forge
      </Button>

      <Dialog open={open} onOpenChange={(next) => !isMoving && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move this arche to Fias Forge?</DialogTitle>
            <DialogDescription>
              Your files are copied into a new forge — a hosted build environment where Claude Code
              works on the project directly. This is one-way: there is no sync back, and this
              builder session becomes read-only. You can still open it to read the history. The
              published arche, its bundle and its earnings are unaffected.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="text-sm text-destructive space-y-2" role="alert">
              <p>{error.message}</p>
              {error.forgeId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(forgePagePath(error.forgeId!))}
                >
                  Open the existing forge
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isMoving}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isMoving}>
              {isMoving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Move to Forge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
